import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Params } from "@angular/router";
import { AuthUserService } from "@app/auth/services/auth-user.service";
import { RouterService } from "@app/root-store/router.service";
import { messages } from "@app/shared/resources/messages";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import {
    agreeToTerms,
    emailValid,
    firstnameEmpty,
    humanNameValid,
    lastnameEmpty,
    passwordValid,
    valuesEqual,
} from "@app/shared/validators";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";

@Component({
    selector: "app-register-form",
    templateUrl: "./register-form.component.html",
    styleUrls: ["./register-form.component.scss"],
})
export class RegisterFormComponent implements OnInit {
    public tryAgainMessage = `Please try the link from your email again or contact your
                Clare dealer or the homeowner to receive a new email invite to ClareHome.`;
    form = this.formBuilder.group(
        {
            firstname: ["", [firstnameEmpty, humanNameValid]],
            lastname: ["", [lastnameEmpty, humanNameValid]],
            email: ["", emailValid],
            password: ["", passwordValid],
            confirmPassword: ["", passwordValid],
            agreeToTerms: ["", agreeToTerms],
        },
        {
            validators: valuesEqual("password", "confirmPassword", "confirmPassword")(messages.validation.passwordsNotEqual),
        }
    );

    isPending$ = this.authUserService.selectPendingSignInState$();
    errorMessage$ = this.authUserService.selectSignInError$();
    queryParams$ = this.authUserService.selectCurrentUrlQueryParams$().pipe(tap(this.updateForm(this.form)));

    constructor(
        private readonly authUserService: AuthUserService,
        private readonly formBuilder: FormBuilder,
        private readonly tsLoggingService: TSLoggingService,
        private readonly routerService: RouterService
    ) {}

    ngOnInit() {
        this.authUserService.dispatchStopPendingLogin();
        this.form.controls.email.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
            this.form.controls.email.setValue((value || "").toLowerCase());
        });
    }

    isEmailInUseError(errorMessage) {
        return errorMessage === "The email address is already in use by another account." || errorMessage?.includes("email-already-in-use");
    }

    goToLogin(inviteCode: string) {
        this.routerService.navigateToUrl([`login`], null, { inviteCode });
    }

    isValidInviteCode(inviteCode: string) {
        return this.authUserService.isValidInviteCode(inviteCode);
    }

    updateForm(form: FormGroup) {
        return (params: Params) => {
            form.patchValue({
                firstname: params?.firstName,
                lastname: params?.lastName,
                email: params?.email,
            });

            if (params?.email) {
                form.controls.email.disable();
            }

            if (!params?.inviteCode) {
                form.disable();
            }
        };
    }

    submit(form: FormGroup, params: Params) {
        const isValidInviteCode = this.isValidInviteCode(params.inviteCode);

        if (form.valid && isValidInviteCode) {
            const lastName: string = form.value.lastname.substring(0, 1).toUpperCase() + form.value.lastname.substring(1);
            const displayName: string = form.value.firstname + " " + lastName;
            const email = form.getRawValue().email;
            const inviteCode = params.inviteCode;

            // We don't want to send the user's password to the TS DB, so we get the password directly from the form here.
            this.authUserService.dispatchRegister(displayName, email, form.value.password, inviteCode);
            this.tsLoggingService.logNoSessionEventData(null, "user", "ssopgrest", "register", null, { displayName, email, inviteCode });
        } else if (isValidInviteCode) {
            this.authUserService.dispatchRegisterFailure("Please complete all required fields.");
        } else {
            this.authUserService.dispatchRegisterFailure(`This sign up page is no longer valid. ${this.tryAgainMessage}`);
        }
    }

    onCancel(queryParams) {
        let route = "login";
        let params;
        this.authUserService.dispatchClearErrorPendingTrue();
        if (queryParams?.staff === "false") {
            route = "signup";
            params = queryParams;
        }
        this.routerService.navigateToUrl([route], null, params);
    }

    get isUnableToSubmit(): boolean {
        return this.form.invalid || this.form.disabled;
    }
}
